import { Dark, LocalStorage } from 'quasar';
import strftime from 'strftime';
import { debounce } from 'quasar';
import { defineStore } from 'pinia';
import type { dynamicObj } from 'src/pages/assets';
import { AppVersion } from 'src/utils/app_version';
import { isUserSetting } from 'src/utils/cacheHelper';
import { objLen, isUndefinedOrNull } from 'src/utils/general';
import { toBoolPreference } from 'src/utils/preferenceBool';
import { isSupplierAdmin, isSupplierUser } from 'src/utils/UserDetails';
import { userApiService, aiModelsAndLabelsApiService } from 'src/services';
import { preferencesService } from 'src/services/preferences/preferencesService';
import { getOrgId } from 'src/utils/organization/org_id_helper';
import { logger } from 'src/utils/logger';
import {
  default_date_pattern,
  version_info_dialog,
  po_match_line_discount,
  all_sub_org_setting,
  CONSTANTS,
  dark_mode_setting,
  date_pattern_setting,
  amount_format_setting,
  doc_script_module_setting,
  models_labels_module_setting,
  po_retention_period,
  open_search_enabled,
  invoice_dashboard_setting,
  supplier_dashboard_setting,
  preferred_language_setting,
  auto_accounting_setting,
  doc_operator_setting,
  grouping_dashboard_setting,
  warehouse_reporting_engine_setting,
  analytics_dashboard_setting,
  quality_analytics_use_database,
  DEFAULT_LANGUAGE,
} from 'src/constants';

export const useSettingStore = defineStore('settings', {
  state: () => ({
    settings: {
      document_processing: true,
      global_settings: true,
    } as { [k: string]: boolean },
    user_settings: {} as dynamicObj,
    org_based_settings: {} as dynamicObj,
    user_based_settings: {} as dynamicObj,
    loading_settings: false,
    app_update_warning: false,
    next_model_training_time: '',
    loading_next_model_training_time: false,
    infor_acl_info: [] as any,
    ionapi: '',
    settingPromiseResolve: null as any,
    settingReqPromise: null as Promise<void> | null,
    cache_data: {} as dynamicObj,
    execute_auto_export: false,
    drawer: true,
  }),
  getters: {
    getSettings(state) {
      return state.settings;
    },
  },
  actions: {
    async deleteCache() {
      const response: dynamicObj = await userApiService.deleteCache();
      return response;
    },

    toggleSettings(setting: string) {
      if (this.settings[setting] === undefined) {
        this.settings[setting] = true;
      } else {
        this.settings[setting] = this.settings[setting] ? false : true;
      }
    },
    async setOrgColor() {
      // 🛡️ FIX: Don't fetch settings if user not logged in (prevents repeated getOrgId calls on login page)
      const org_id = getOrgId();
      if (!org_id) {
        logger.warn('[SETTINGS] ⚠️ setOrgColor skipped - user not logged in');
        return;
      }

      if (!this.user_settings['ORG_COLOR_PREFERENCE']) {
        await this.startSettingsFetch();
        if (!this.user_settings['ORG_COLOR_PREFERENCE']) {
          return;
        }
      }
      document.documentElement.style.setProperty(
        '--primary-color',
        this.user_settings['ORG_COLOR_PREFERENCE'],
      );
    },

    // async setOrgSettings () {
    //   await this.startSettingsFetch();
    //   saveSettings(this.user_settings);
    // },

    async setUserSettings() {
      try {
        this.loading_settings = true;

        const response = await preferencesService.getAllUserPreferences();
        if (!response || Object.keys(response).length === 0) {
          // ✅ Log warning but don't throw - allow app to continue with empty preferences
          logger.warn(
            '[SETTINGS] getAllUserPreferences() returned empty response. Using default preferences.',
          );
          return;
        }
        for (const [key, value] of Object.entries(response)) {
          this.user_settings[key] = this.getSettingValue(value);
          this.user_based_settings[key] = this.getSettingValue(value);
        }
        Dark.set(this.user_settings[dark_mode_setting]);
      } catch (err) {
        // ✅ Log error but don't re-throw - allow app to continue with empty preferences
        logger.error('[SETTINGS] ❌ Failed to fetch user preferences:', err);
        logger.warn(
          '[SETTINGS] ⚠️ Using default/empty user preferences. App will continue with limited functionality.',
        );
      } finally {
        this.loading_settings = false;
      }
    },

    clearSubOrgIdPromise() {
      this.settingPromiseResolve = null;
      this.settingReqPromise = null;
    },

    async startSettingsFetch() {
      // ✅ Skip if settings already loaded - prevents duplicate fetches
      if (this.user_settings && Object.keys(this.user_settings).length > 0) {
        logger.debug('[SETTINGS] ℹ️ Settings already loaded - skipping redundant fetch');
        return;
      }

      if (this.settingReqPromise) {
        // ✅ Return existing promise if already fetching
         
        return this.settingReqPromise;
      }

      // ✅ Create promise that ALWAYS resolves (never rejects) - ensures app doesn't crash
      this.settingReqPromise = new Promise((resolve) => {
        this.settingPromiseResolve = resolve;
        // ✅ Call getOrgSettings() without awaiting here - it handles errors internally
        this.getOrgSettings().catch((err) => {
          // Should not happen since getOrgSettings() catches errors, but be extra safe
          logger.warn('[SETTINGS] Unexpected error in startSettingsFetch():', err);
        });
      });

      return this.settingReqPromise;
    },

    async getOrgSettings() {
      try {
        this.loading_settings = true;
        this.user_settings = {} as { [k: string]: any };
        const response = await userApiService.getOrgPreferences();
        if (!response) {
          throw response;
        }
        for (const [key, value] of Object.entries(response)) {
          this.org_based_settings[key] = this.getSettingValue(value);
          if (isUserSetting(key)) {
            continue;
          }

          this.user_settings[key] = this.getSettingValue(value);
        }
        await this.setUserSettings();
        this.setDefaultPreference();
      } catch (err) {
        throw err;
      } finally {
        this.loading_settings = false;

        if (this.settingPromiseResolve) {
          this.settingPromiseResolve();
        }
        if (isSupplierAdmin() || isSupplierUser()) {
          this.validateSupplierSetting()
        }
      }
    },
    async validateSupplierSetting() {
      try {
        logger.debug('[SETTINGS] Validating supplier dashboard settings');
        const keys = ['INVOICE_DASHBOARD', 'PO_DASHBOARD'];
        for (const key of keys) {
          if (this.user_settings[key] == undefined) {
            await this.updateSetting(key, true);
          }
        }
        logger.info('[SETTINGS] Supplier dashboard settings validated successfully');
      } catch (err) {
        logger.error('[SETTINGS] Failed to validate supplier settings:', err);
        throw err;
      }
    },
    getSettingValue(value: any): any {
      let data = value;

      if (typeof value === 'string') {
        const lowercase_value = value.toLowerCase();
        if (lowercase_value === 'true' || lowercase_value === 'false') {
          data = lowercase_value === 'true';
        }
      }

      return data;
    },

    setSetting(key: string, value: any) {
      this.user_settings[key] = this.getSettingValue(value);
    },

    // async getSetting (key: string): Promise<any> {
    //   if (objLen(this.user_settings) !== 0 || !this.user_settings.hasOwnProperty(key)) {
    //     await this.fetchSettingFromServer(key);
    //   }

    //   // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    //   return this.user_settings[key];
    // },

    // async getSettingWithExpiry (key: string, default_val?:any ): Promise<any> {
    //   try {
    //     this.loading_settings = true;
    //     if (isUserSetting(key)) {
    //       return this.getUserBasedSettings(key, default_val);
    //     }

    //     const setting = getWithExpiry(key) as dynamicObj;
    //     if (setting !== undefined || setting !== null) {
    //       this.user_settings[key] = setting;
    //     }

    //     if (objLen(this.user_settings) === 0 || !this.user_settings.hasOwnProperty(key)) {
    //       await this.fetchSettingFromServer(key);

    //       if (!this.user_settings.hasOwnProperty(key)) {
    //         this.user_settings[key] = default_val || false;
    //       }
    //       setWithExpiry(key, this.user_settings[key]);
    //     }

    //     // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    //     return this.user_settings[key];
    //   } catch (err) {
    //     throw err;
    //   }  finally {
    //     this.loading_settings = false;
    //   }
    // },

    // handleStorageEvent (event: any) {
    //   try {

    //     if (event.key === 'user_settings') {
    //       const user_settings = getSettingsFromStorage();
    //       for (const [key, value] of Object.entries(user_settings)) {
    //         this.syncSetting(key, value);
    //       }
    //     }
    //   } catch (err) {
    //     logError('error in storage event', err);
    //   }

    // },

    // syncSetting (key: string, value:any ) {
    //   const current_value = this.user_settings[key];
    //   if (current_value === value) {
    //     return;
    //   }

    //   this.user_settings[key] = value;
    //   setWithExpiry(key, this.user_settings[key]);
    // },

    async getOrgBasedSettings(key: string): Promise<any> {
      // Wait for settings to be loaded if they're currently being fetched
      if (this.settingReqPromise) {
        await this.settingReqPromise;
      }
      return this.org_based_settings[key];
    },

    async getUserBasedSettings(key: string): Promise<any> {
      // Wait for settings to be loaded if they're currently being fetched
      if (this.settingReqPromise) {
        await this.settingReqPromise;
      }
      return this.user_based_settings[key];
    },

    async getSetting(key: string): Promise<any> {
      // Wait for settings to be loaded if they're currently being fetched
      if (this.settingReqPromise) {
        await this.settingReqPromise;
      }
      return this.user_settings[key];
    },

    // async getUserSettingWithExpiry (key: string, default_val?:any ): Promise<any> {
    //   try {
    //     this.loading_settings = true;

    //     const setting = getWithExpiry(key, true) as dynamicObj;
    //     if (setting) {
    //       this.user_settings[key] = setting;
    //     }

    //     if (objLen(this.user_settings) === 0 || !this.user_settings.hasOwnProperty(key)) {

    //       const value =  await userApiService.getUserPreference({key});
    //       this.setSetting(key, value);
    //       if (isUndefinedOrNull(this.user_settings[key])) {
    //         this.user_settings[key] = default_val || false;
    //       }
    //       setWithExpiry(key, this.user_settings[key], true);
    //     }

    //     // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    //     return this.user_settings[key];
    //   } catch (err) {
    //     throw err;
    //   }  finally {
    //     this.loading_settings = false;
    //   }
    // },

    // organization settings
    async updateSetting(key: string, value: any) {
      try {
        key = key.toUpperCase();
        logger.debug(`[SETTINGS] Updating setting ${key} to ${value}`);

        // Validate value - prevent undefined/null from reaching API
        // This prevents the backend error: "set_organisation_preference_async() missing 1 required positional argument: 'value'"
        if (value === undefined || value === null) {
          logger.warn(
            `[SETTINGS] updateSetting called with undefined/null value for key: ${key}. Defaulting to false.`,
          );
          value = false;
        }

        // Booleans are sent as true/false (the service String()-ifies them to
        // "true"/"false"), and getSettingValue() coerces those back to real
        // booleans on read — so the value round-trips as a boolean. No 1/0
        // normalization needed; that only added a string-vs-boolean mismatch.

        // updateSetting handles organization-level preferences only.
        // User-level preferences (e.g. PREFERRED_LANGUAGE) go through
        // setUserPreferences() — callers invoke that directly.
        const response = await preferencesService.setOrgPreference(key, value);

        if (!response.success) {
          throw new Error(response.error || 'Failed to set org preference');
        }

        // Store the original boolean value in the store for component binding
        // as user setting has more preference so only update the setting in the main object if it is not in the user settings
        if (!this.user_based_settings[key]) {
          this.user_settings[key] = JSON.parse(JSON.stringify(value));
        }

        this.org_based_settings[key] = JSON.parse(JSON.stringify(value));
        logger.info(`[SETTINGS] Successfully updated org setting ${key}`);
      } catch (err) {
        logger.error('[SETTINGS] Failed to update setting:', { key, value, error: err });
        throw err;
      }
    },

    // also organization level settings
    async updateDashboardGroupingSetting(
      key: string,
      value: string,
      document_type = 'INVOICE',
    ) {
      try {
        logger.debug('[SETTINGS] Updating dashboard grouping setting:', { key, value, document_type });
        const response = await preferencesService.setDashboardGroupingPreference(
          key,
          value,
          document_type,
        );

        if (!response.success) {
          throw new Error(
            response.error || 'Failed to set dashboard grouping preference',
          );
        }
        this.user_settings[key] = JSON.parse(JSON.stringify(value));
        this.org_based_settings[key] = JSON.parse(JSON.stringify(value));
        logger.info('[SETTINGS] Dashboard grouping setting updated successfully:', key);
      } catch (err) {
        logger.error('[SETTINGS] Failed to update dashboard grouping setting:', { key, value, document_type, error: err });
        throw err;
      }
    },

    // also organization level settings
    async updateReportingEngineSetting(key: string, value: string) {
      try {
        logger.debug('[SETTINGS] Updating reporting engine setting:', { key, value });
        const response = await preferencesService.setReportingEnginePreference(
          key,
          value,
        );

        if (!response.success) {
          throw new Error(
            response.error || 'Failed to set reporting engine preference',
          );
        }
        this.user_settings[key] = JSON.parse(JSON.stringify(value));
        this.org_based_settings[key] = JSON.parse(JSON.stringify(value));
        logger.info('[SETTINGS] Reporting engine setting updated successfully:', key);
        return response as dynamicObj;
      } catch (err) {
        logger.error('[SETTINGS] Failed to update reporting engine setting:', { key, value, error: err });
        throw err;
      }
    },

    // also organization level settings
    async updateDBDirectAccessSetting(key: string, value: boolean) {
      try {
        logger.debug('[SETTINGS] Updating DB direct access setting:', { key, value });
        const response = await userApiService.getDBCredentials({
          access_key: key,
          enable_user: value,
        });

        if (response.success === false) {
          throw response;
        }
        this.user_settings[key] = JSON.parse(JSON.stringify(value));
        this.org_based_settings[key] = JSON.parse(JSON.stringify(value));
        logger.info('[SETTINGS] DB direct access setting updated successfully:', key);
        return response as dynamicObj;
      } catch (err) {
        logger.error('[SETTINGS] Failed to update DB direct access setting:', { key, value, error: err });
        throw err;
      }
    },

    // also organization level settings
    async updateGroupingPurchaseOrderSetting(key: string, value: string) {
      try {
        logger.debug('[SETTINGS] Updating PO grouping setting:', { key, value });
        const response =
          await preferencesService.setPurchaseOrderGroupingPreference(
            key,
            value,
          );

        if (!response.success) {
          throw new Error(
            response.error || 'Failed to set PO grouping preference',
          );
        }
        this.user_settings[key] = JSON.parse(JSON.stringify(value));
        this.org_based_settings[key] = JSON.parse(JSON.stringify(value));
        logger.info('[SETTINGS] PO grouping setting updated successfully:', key);
        return response as dynamicObj;
      } catch (err) {
        logger.error('[SETTINGS] Failed to update PO grouping setting:', { key, value, error: err });
        throw err;
      }
    },

    // organization settings
    async fetchSettingFromServer(key: string): Promise<any> {
      // PREFERRED_LANGUAGE must be fetched per user, not per organization
      if (key.toUpperCase() === preferred_language_setting.toUpperCase()) {
        // ⭐ SDK wrapper handles org_id automatically from JWT token
        const response = await preferencesService.getUserPreference(key);
        if (!response) {
          return;
        }

        this.user_settings[key] = this.getSettingValue(response);
        this.user_based_settings[key] = this.getSettingValue(response);
      } else {
        const response = await preferencesService.getOrgPreference(key);
        if (!response) {
          return;
        }

        this.user_settings[key] = this.getSettingValue(response);
        this.org_based_settings[key] = this.getSettingValue(response);
      }
    },

    checkVersionUpdate() {
      const is_required = AppVersion.isRefershReq();
      if (!is_required) {
        return;
      }

      if (this.user_settings[version_info_dialog]) {
        this.app_update_warning = true;
      }
    },

    async getNextModelTrainingTime(): Promise<any> {
      try {
        this.loading_next_model_training_time = true;
        const response =
          await aiModelsAndLabelsApiService.getNextModelTrainingTime();
        if (!response) {
          throw response;
        }

        if (
          !(objLen(response) !== 0 && response.hasOwnProperty('next_schedule'))
        ) {
          return;
        }

        this.next_model_training_time = response.next_schedule;
        return this.next_model_training_time;
      } catch (err) {
        throw err;
      } finally {
        this.loading_next_model_training_time = false;
      }
    },

    async getAclInfo() {
      try {
        logger.debug('[SETTINGS] Fetching Infor ACL info');
        const response = await userApiService.getInforAclInfo();
        if (!response) {
          throw response;
        }
        this.infor_acl_info = Array.isArray(response.mapping)
          ? response.mapping
          : [];
        this.ionapi = response.ion?.ionapi || '';
        logger.info('[SETTINGS] Infor ACL info fetched successfully');
      } catch (err) {
        logger.error('[SETTINGS] Failed to fetch Infor ACL info:', err);
        throw err;
      }
    },

    async createAclInitializer(data: FormData) {
      try {
        logger.debug('[SETTINGS] Creating Infor ACL initializer');
        const response = await userApiService.createAclInitializer(data);
        if (!response) {
          throw response;
        }
        this.infor_acl_info = Array.isArray(response.mapping)
          ? response.mapping
          : [];
        this.ionapi = response.ion?.ionapi || '';
        logger.info('[SETTINGS] Infor ACL initializer created successfully');
      } catch (err) {
        logger.error('[SETTINGS] Failed to create Infor ACL initializer:', err);
        throw err;
      }
    },

    async createAclMapping(data: dynamicObj) {
      try {
        logger.debug('[SETTINGS] Creating Infor ACL mapping:', data);
        const response = await userApiService.createAclMapping(data);
        if (!response) {
          throw response;
        }
        this.infor_acl_info.push(response.mapping);
        logger.info('[SETTINGS] Infor ACL mapping created successfully');
      } catch (err) {
        logger.error('[SETTINGS] Failed to create Infor ACL mapping:', { data, error: err });
        throw err;
      }
    },

    async deleteAclSetting() {
      try {
        logger.debug('[SETTINGS] Deleting Infor ACL setting');
        const response = await userApiService.deleteAclSetting();
        if (!response) {
          throw response;
        }
        this.infor_acl_info = [];
        this.ionapi = '';
        logger.info('[SETTINGS] Infor ACL setting deleted successfully');
      } catch (err) {
        logger.error('[SETTINGS] Failed to delete Infor ACL setting:', err);
        throw err;
      }
    },

    async deleteAclMapping(map_id: string) {
      try {
        logger.debug('[SETTINGS] Deleting Infor ACL mapping:', map_id);
        const response = await userApiService.deleteAclMapping(map_id);
        if (!response) {
          throw response;
        }
        this.infor_acl_info.forEach((element: dynamicObj) => {
          if (element.id == map_id) {
            this.infor_acl_info.splice(this.infor_acl_info.indexOf(element), 1);
          }
        });
        logger.info('[SETTINGS] Infor ACL mapping deleted successfully:', map_id);
      } catch (err) {
        logger.error('[SETTINGS] Failed to delete Infor ACL mapping:', { map_id, error: err });
        throw err;
      }
    },
    // Internal debounced API call (stable instance)
    _debouncedApiCall: debounce(async function (
      this: any,
      key: string,
      value: any,
    ) {
      await this._callSetUserPreferenceApi(key, value);
    }, 3000),

    // API call logic (extracted to be reusable by both debounced and direct)
    async _callSetUserPreferenceApi(key: string, value: any) {
      // Abort silently when org_id is absent (logout race / unauthenticated state)
      if (!getOrgId()) {
        logger.warn('[SETTINGS] setUserPreference skipped - no org_id available');
        return;
      }

      const response = await preferencesService.setUserPreference(
        key.toUpperCase(),
        value,
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to set user preference');
      }
    },

    // Public method with optional debounce
    async setUserPreferences(key: string, value: any, debounceSave = false) {
      try {
        // Wait for the initial prefs fetch before writing, so we don't race the GET.
        if (this.settingReqPromise) {
          await this.settingReqPromise;
        }

        if (debounceSave) {
          this._debouncedApiCall(key, value);
        } else {
          await this._callSetUserPreferenceApi(key, value);
        }

        this.user_settings[key] = JSON.parse(JSON.stringify(value));
        this.user_based_settings[key] = JSON.parse(JSON.stringify(value));
      } catch (err) {
        throw err;
      }
    },

    async setCachePreferences(enable: boolean, ttl: number) {
      try {
        const response = await preferencesService.setCachePreference(
          'CACHE_POC',
          'POC',
          enable,
          ttl,
        );
        if (!response.success) {
          throw new Error(response.error || 'Failed to set cache preference');
        }
      } catch (err) {
        throw err;
      }
    },

    async getCachePreferences(key: string) {
      try {
        const response = await preferencesService.getCachePreference(key);
        if (!response) {
          return; // No cache preference found, not an error
        }
        this.cache_data[key] = response[key] || {};
        this.user_settings[key] = JSON.stringify(response[key]);
      } catch (err) {
        throw err;
      }
    },

    dateFormatOnly(date: string | Date | null) {
      if (!date) {
        return '';
      } else {
        const { DATE_PATTERN } = this.user_settings;
        if (DATE_PATTERN) {
          return strftime(DATE_PATTERN, new Date(date));
        }
        return date;
      }
    },

    datePattern(date: string | Date | null) {
      if (!date) {
        return '';
      }
      const date_pattern = { ...this.user_settings };
      let date_format = '';
      if (date_pattern.DATE_PATTERN == '') {
        date_pattern.DATE_PATTERN = '%Y %m,%d %H:%M:%S';
      } else {
        date_pattern.DATE_PATTERN += ' %H:%M:%S';
      }
      date_format = date_pattern.DATE_PATTERN;
      const dateFormated = strftime(date_format, new Date(date));
      return dateFormated;
    },

    statusInterface(status: string) {
      let color = '';
      switch (status) {
        case 'Invoiced':
          color = '#ffa31a';
          break;
        case 'Success':
          color = '#00b300';
          break;
        case 'Failed':
          color = '#cc0000';
          break;
        case 'Closed':
          color = '#cc0000';
          break;
        default:
          color = '#2388ae';
          break;
      }
      return `<span style="color: ${color};">${status}</span>`;
    },
    setDefaultPreference() {
      if (!this.user_settings.banner_color) {
        this.user_settings['banner_color'] = '#2388AE';
      }
      if (!this.user_settings.banner_text_color) {
        this.user_settings['banner_text_color'] = '#ffffff';
      }

      if (!this.user_settings[date_pattern_setting]) {
        this.user_settings[date_pattern_setting] = default_date_pattern;
      }

      if (!this.user_settings['AMOUNTS_ROUNDING']) {
        this.user_settings['AMOUNTS_ROUNDING'] = false;
      }

      if (!this.user_settings['ACTION_RESTART']) {
        this.user_settings['ACTION_RESTART'] = 'only_admin_assignee';
      }

      if (!this.user_settings[all_sub_org_setting]) {
        this.user_settings[all_sub_org_setting] = false;
      }

      if (this.user_settings['ORG_COLOR_PREFERENCE']) {
        document.documentElement.style.setProperty(
          '--primary-color',
          this.user_settings['ORG_COLOR_PREFERENCE'],
        );
      }

      if (!this.user_settings[CONSTANTS.dual_monitor_mode_setting]) {
        this.user_settings[CONSTANTS.dual_monitor_mode_setting] = false;
      }

      if (!this.user_settings[dark_mode_setting]) {
        this.user_settings[dark_mode_setting] = false;
      }

      if (!this.user_settings[date_pattern_setting]) {
        this.user_settings[date_pattern_setting] = '%d/%m/%Y';
      }

      if (!this.user_settings[amount_format_setting]) {
        this.user_settings[amount_format_setting] = 'de_DE';
      }

      if (!this.user_settings[doc_script_module_setting]) {
        this.user_settings[doc_script_module_setting] = false;
      }

      if (!this.user_settings[CONSTANTS.INBOUND_EMAILS]) {
        this.user_settings[CONSTANTS.INBOUND_EMAILS] = false;
      }

      if (!this.user_settings[CONSTANTS.INFOR_ACL_UPDATER_SETTING]) {
        this.user_settings[CONSTANTS.INFOR_ACL_UPDATER_SETTING] = false;
      }

      if (!this.user_settings[CONSTANTS.DOC_SCAN_MODULE_SETTING]) {
        this.user_settings[CONSTANTS.DOC_SCAN_MODULE_SETTING] = false;
      }

      if (!this.user_settings[models_labels_module_setting]) {
        this.user_settings[models_labels_module_setting] = false;
      }

      if (!this.user_settings['PAGINATION_SIZE']) {
        this.user_settings['PAGINATION_SIZE'] = 25;
      }

      if (!this.user_settings['TASK_PAGINATION_SIZE']) {
        this.user_settings['TASK_PAGINATION_SIZE'] = 10;
      }

      if (!this.user_settings[CONSTANTS.docnet_setting]) {
        this.user_settings[CONSTANTS.docnet_setting] = false;
      }

      if (!this.user_settings[CONSTANTS.auto_refresh_dashboard_on_update]) {
        this.user_settings[CONSTANTS.auto_refresh_dashboard_on_update] = false;
      }

      if (!this.user_settings[CONSTANTS.selected_po_retention_period]) {
        this.user_settings[CONSTANTS.selected_po_retention_period] =
          po_retention_period[1].value;
      }

      if (!this.user_settings[invoice_dashboard_setting]) {
        this.user_settings[invoice_dashboard_setting] = false;
      }

      if (!this.user_settings[open_search_enabled]) {
        this.user_settings[open_search_enabled] = false;
      }

      if (!this.user_settings[CONSTANTS.supplier_statistics_setting]) {
        this.user_settings[CONSTANTS.supplier_statistics_setting] = false;
      }

      if (!this.user_settings[supplier_dashboard_setting]) {
        this.user_settings[supplier_dashboard_setting] = false;
      }

      if (!this.user_settings[CONSTANTS.po_auto_match_and_export]) {
        this.user_settings[CONSTANTS.po_auto_match_and_export] = false;
      }

      if(!this.user_settings[analytics_dashboard_setting]) {
        this.user_settings[analytics_dashboard_setting] = false;
      }

      // Quality Analytics PostgreSQL toggle - default to false (use Prometheus)
      if (
        this.user_settings[quality_analytics_use_database] === null ||
        this.user_settings[quality_analytics_use_database] === undefined
      ) {
        this.user_settings[quality_analytics_use_database] = false;
      }

      if (!this.user_settings[CONSTANTS.only_admin_can_save_rules]) {
        this.user_settings[CONSTANTS.only_admin_can_save_rules] = false;
      }

      // Fix: Ensure auto_accounting_setting has a boolean value to prevent indeterminate toggle state
      if (
        this.user_settings[auto_accounting_setting] === null ||
        this.user_settings[auto_accounting_setting] === undefined
      ) {
        this.user_settings[auto_accounting_setting] = false;
      }

      // Fix: Ensure workflow builder settings have boolean values to prevent indeterminate toggle state
      if (
        this.user_settings['WORKFLOW_BUILDER_MODULE_SETTING'] === null ||
        this.user_settings['WORKFLOW_BUILDER_MODULE_SETTING'] === undefined
      ) {
        this.user_settings['WORKFLOW_BUILDER_MODULE_SETTING'] = false;
      }

      if (
        this.user_settings['WORKFLOW_CELERY_SETTING'] === null ||
        this.user_settings['WORKFLOW_CELERY_SETTING'] === undefined
      ) {
        this.user_settings['WORKFLOW_CELERY_SETTING'] = false;
      }

      // Fix: Ensure layout builder setting has boolean value to prevent indeterminate toggle state
      if (
        this.user_settings['LAYOUT_BUILDER_MODULE_SETTING'] === null ||
        this.user_settings['LAYOUT_BUILDER_MODULE_SETTING'] === undefined
      ) {
        this.user_settings['LAYOUT_BUILDER_MODULE_SETTING'] = false;
      }

      // Fix: Ensure DOC Operator setting has boolean value to prevent indeterminate toggle state
      if (
        this.user_settings[doc_operator_setting] === null ||
        this.user_settings[doc_operator_setting] === undefined
      ) {
        this.user_settings[doc_operator_setting] = false;
      }

      // Fix: Ensure grouping dashboard setting has boolean value to prevent indeterminate toggle state
      if (
        this.user_settings[grouping_dashboard_setting] === null ||
        this.user_settings[grouping_dashboard_setting] === undefined
      ) {
        this.user_settings[grouping_dashboard_setting] = false;
      }

      // Fix: Ensure warehouse reporting engine setting has boolean value to prevent indeterminate toggle state
      if (
        this.user_settings[warehouse_reporting_engine_setting] === null ||
        this.user_settings[warehouse_reporting_engine_setting] === undefined
      ) {
        this.user_settings[warehouse_reporting_engine_setting] = false;
      }

      if (!this.user_settings[preferred_language_setting]) {
        this.user_settings[preferred_language_setting] = DEFAULT_LANGUAGE;
        this.user_based_settings[preferred_language_setting] = DEFAULT_LANGUAGE;
      }

      if (!this.user_settings[CONSTANTS.vertex_tax_integration_setting]) {
        this.user_settings[CONSTANTS.vertex_tax_integration_setting] = false;
      }
    },

    isPOLineDiscountSet() {
      if (isUndefinedOrNull(this.user_settings[po_match_line_discount])) {
        return false;
      }
      return toBoolPreference(this.user_settings[po_match_line_discount]);
    },
  },
});
